import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";

const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));
const PERIODS = ["AM", "PM"] as const;

interface TimePickerProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

function parse24h(value?: string): { hour: string; minute: string; period: string } {
  if (!value) return { hour: "12", minute: "00", period: "AM" };
  const [hStr, mStr] = value.split(":");
  let h = parseInt(hStr, 10);
  const period = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  // Round minute to nearest 5
  const rawMin = parseInt(mStr, 10);
  const rounded = Math.round(rawMin / 5) * 5;
  const minute = String(rounded >= 60 ? 55 : rounded).padStart(2, "0");
  return { hour: String(h), minute, period };
}

function to24h(hour: string, minute: string, period: string): string {
  let h = parseInt(hour, 10);
  if (period === "PM" && h < 12) h += 12;
  if (period === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${minute}`;
}

function formatDisplay(hour: string, minute: string, period: string): string {
  return `${hour}:${minute} ${period}`;
}

const TimePicker = ({ value, onChange, placeholder = "Pick a time" }: TimePickerProps) => {
  const parsed = parse24h(value);
  const [hour, setHour] = useState(parsed.hour);
  const [minute, setMinute] = useState(parsed.minute);
  const [period, setPeriod] = useState(parsed.period);

  useEffect(() => {
    const p = parse24h(value);
    setHour(p.hour);
    setMinute(p.minute);
    setPeriod(p.period);
  }, [value]);

  const handleChange = (h: string, m: string, p: string) => {
    onChange(to24h(h, m, p));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground"
          )}
        >
          <Clock className="mr-2 h-4 w-4" />
          {value ? formatDisplay(hour, minute, period) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3 pointer-events-auto" align="start">
        <div className="flex items-center gap-2">
          <Select
            value={hour}
            onValueChange={(v) => {
              setHour(v);
              handleChange(v, minute, period);
            }}
          >
            <SelectTrigger className="w-[68px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HOURS.map((h) => (
                <SelectItem key={h} value={h}>{h}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <span className="text-muted-foreground font-semibold">:</span>

          <Select
            value={minute}
            onValueChange={(v) => {
              setMinute(v);
              handleChange(hour, v, period);
            }}
          >
            <SelectTrigger className="w-[68px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MINUTES.map((m) => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={period}
            onValueChange={(v) => {
              setPeriod(v);
              handleChange(hour, minute, v);
            }}
          >
            <SelectTrigger className="w-[72px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { TimePicker };
